import * as React from "react"
import { Box, Chip, Link, Rating, Typography } from "@mui/material"
import { FlagsIconSeeker, IconSeeker, ImageSeeker } from "@ui-seeker/core"

import { imageViewer } from "../@signals"

//: Every cell gets the same ruling and the same vertical rhythm. The rows here
//: are tall and uneven — Product Raw is five lines, Images is one digit — so
//: without a column rule and a shared top alignment the values float and it is
//: hard to tell which number belongs to which row.
const CELL_STYLE = {
    borderLeft: '1px solid #f1f5f9',
    verticalAlign: 'top',
    paddingTop: '14px',
    paddingBottom: '14px',
}

const cellProps = () => ({ style: CELL_STYLE })
const headProps = () => ({ style: { borderLeft: '1px solid #f1f5f9', verticalAlign: 'bottom' } })

//: The data columns are centred, the two composite ones are not. A single
//: number or a dash floating on the left of a wide column is hard to tie to its
//: row; Retailer info and Product Raw are paragraphs and read left-aligned.
//:
//: Centred both ways: the row is top-aligned for those two paragraphs, and a
//: lone price or rating pinned to the top of a 140px-tall row sat nowhere near
//: the product it belongs to.
const centeredCell = () => ({
    style: { ...CELL_STYLE, textAlign: 'center', verticalAlign: 'middle' },
})
const centeredHead = () => ({
    style: { borderLeft: '1px solid #f1f5f9', verticalAlign: 'bottom', textAlign: 'center' },
})


/** Open this page's viewer. One image here; the drawer opens whole galleries. */
function openImage(url, title) {
    if (!url) return
    imageViewer.value = { urls: [url], index: 0, title: title || 'Image' }
}

const CURRENCY_SYMBOLS = {
    USD: '$', EUR: '€', GBP: '£', MXN: '$', BRL: 'R$', ARS: '$', CLP: '$',
    COP: '$', PEN: 'S/', CAD: 'C$', CHF: 'CHF', SEK: 'kr', PLN: 'zł',
}

const STALE_AFTER_DAYS = 7


function formatPrice(value, currency) {
    if (value === null || value === undefined || value === '') return null
    const symbol = CURRENCY_SYMBOLS[currency] || currency || ''
    // A space: '$58219900.00' ran together and was unreadable.
    return symbol ? `${symbol} ${Number(value).toFixed(2)}` : Number(value).toFixed(2)
}


function daysSince(dateString) {
    if (!dateString) return null
    return Math.floor((new Date() - new Date(dateString)) / (1000 * 60 * 60 * 24))
}


/**
 * The column label, with its field coverage folded in: `Images (100.00%)`.
 *
 * This is where the percentages live — in the header and, because it is the
 * same string, in the column chooser. There is no separate coverage panel: a
 * strip of thirty-odd cards above the grid is a wall, and the number only means
 * anything next to the field it describes.
 *
 * The percentage is over every row the filters match, not the page.
 */
function withCoverage(label, coverage, key) {
    const value = coverage?.[key]
    if (value === undefined || value === null) return label
    return `${label} (${value.toFixed(2)}%)`
}


/**
 * The colour of a coverage figure.
 *
 * A measured **0 % is the loudest thing on the screen**, not a greyed-out
 * "no data": it is the answer this dashboard exists to give. Everything else
 * shades from amber towards green as the scraper covers more of the field.
 */
function coverageColours(value) {
    if (value === 0) return { fg: '#b91c1c', bg: '#fef2f2' }
    if (value < 50) return { fg: '#b45309', bg: '#fffbeb' }
    if (value < 95) return { fg: '#0369a1', bg: '#f0f9ff' }
    return { fg: '#15803d', bg: '#f0fdf4' }
}


/**
 * A header that carries its coverage as a coloured pill under the name.
 *
 * The plain `label` string still holds the percentage — that is what the column
 * chooser shows, and it has to stay readable there — but in the header itself
 * the number is worth colouring: scanning a row of headers for the red ones is
 * how you find what a scraper is missing without reading a single figure.
 */
function headerWithCoverage(label, coverage, key) {
    const value = coverage?.[key]

    return () => (
        <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.25, py: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' }}>
                {label}
            </Typography>
            {value !== undefined && value !== null && (
                <Box
                    sx={{
                        alignSelf: 'flex-start',
                        px: 0.75,
                        borderRadius: 0.75,
                        fontSize: 11,
                        fontWeight: 700,
                        lineHeight: 1.6,
                        color: coverageColours(value).fg,
                        backgroundColor: coverageColours(value).bg,
                    }}
                    title={`${label}: ${value.toFixed(2)}% of the filtered products carry this field`}
                >
                    {value.toFixed(2)}%
                </Box>
            )}
        </Box>
    )
}


/** A value the scraper did not bring. Not an error — the finding itself. */
function Missing() {
    return <Typography component="span" variant="body2" sx={{ color: '#d1d5db' }}>—</Typography>
}


/** 0/1 flags arrive as numbers on purpose: React renders nothing for a boolean. */
function flagCell(value) {
    if (value === null || value === undefined) return <Missing />
    return value
        ? <IconSeeker icon="check" sx={{ fontSize: 14, color: '#15803d' }} />
        : <IconSeeker icon="xmark" sx={{ fontSize: 14, color: '#d1d5db' }} />
}


function countCell(value) {
    return value ? value : <Missing />
}


function numberCell(value) {
    if (value === null || value === undefined || value === '') return <Missing />
    return <Typography variant="body2">{Number(value).toFixed(2)}</Typography>
}


/** Money, with its row's currency. Every price column renders the same way. */
function priceCell(value) {
    const formatted = formatPrice(value?.value, value?.currency)
    if (!formatted) return <Missing />
    // nowrap: the space added for legibility let a narrow column break the
    // line between the symbol and the amount, leaving a lone '$' on top.
    return <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
        {formatted}
    </Typography>
}


function textCell(value) {
    if (value === null || value === undefined || value === '') return <Missing />
    // Native title rather than a MUI Tooltip: one Tooltip per cell is expensive
    // on tables this size, and its ref cloning is what blanks the app under a
    // dev build (see the workspace CLAUDE.md).
    return <Typography
        variant="body2"
        title={String(value)}
        sx={{ maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
    >
        {value}
    </Typography>
}


/** `Label: value`, the shape both composite cells are built out of. */
function Field({ label, children }) {
    return (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'baseline', lineHeight: 1.5 }}>
            {/* nowrap + no shrink: a long value squeezed the label until
                "Brand raw:" broke into two lines. */}
            <Typography
                variant="caption"
                sx={{ color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
                {label}
            </Typography>
            {children}
        </Box>
    )
}


/** A yes/no the eye can scan down a column. */
function YesNo({ value }) {
    return <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: value ? '#15803d' : '#b91c1c' }}
    >
        {value ? 'YES' : 'NO'}
    </Typography>
}


/**
 * "4 sellers", and in amber when the row is not showing the buybox one.
 *
 * DIPI_LAST_ITEM_RAW keeps the last capture written, so on a marketplace the
 * table shows whichever seller was scraped last — for product 1566303504 on
 * Otto.de that was DS Trading GmbH at 69,90 € while OTTO held the buybox at
 * 16,49 €. That mismatch is the finding, hence the colour and the tooltip.
 *
 * Absent until the offers request lands, and for products with one seller.
 */
function SellersChip({ info, sellerName, sellerCode, onOpen }) {
    if (!info || info.offers < 2) return null

    // By code where the site has codes (Amazon writes a merchant id in both
    // tables), by name where it does not (Otto writes the shop name in both,
    // and the raw row's seller_code is empty). Comparing the row's NAME against
    // the offer's CODE — which is what this did first — reads as "not buybox"
    // on every Amazon row, including the ones that are the buybox.
    const shown = (sellerCode || sellerName || '').trim().toLowerCase()
    const buybox = (sellerCode ? info.buyboxSeller : info.buyboxSellerName) || ''
    const mismatch = Boolean(shown && buybox && shown !== buybox.trim().toLowerCase())
    const money = (amount) => (amount == null ? '—' : `${info.currency || ''} ${Number(amount).toFixed(2)}`)

    const title = mismatch
        ? `Buybox: ${info.buyboxSellerName || info.buyboxSeller} — ${money(info.buyboxTotal)}\n`
            + `Shown: ${sellerName || sellerCode} — the last seller captured\n`
            + `${info.offers} offers that day, ${info.externals} from other sellers`
        : `${info.offers} offers that day, ${info.externals} from other sellers\n`
            + `Cheapest ${money(info.cheapestTotal)} · dearest ${money(info.dearestTotal)}`

    return (
        <Chip
            size="small"
            label={mismatch ? `${info.offers} sellers · not buybox` : `${info.offers} sellers`}
            title={title}
            onClick={onOpen}
            sx={{
                mt: 0.5, height: 18, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                color: mismatch ? '#b45309' : '#1d4ed8',
                backgroundColor: mismatch ? '#fffbeb' : '#eff6ff',
                border: `1px solid ${mismatch ? '#fde68a' : '#bfdbfe'}`,
                '&:hover': { backgroundColor: mismatch ? '#fef3c7' : '#dbeafe' },
            }}
        />
    )
}


function RetailerInfoCell({ value, onShowOffers }) {
    if (!value) return <Missing />
    return (
        <Box sx={{ minWidth: 170 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                {value.countryCode && <FlagsIconSeeker countryName={value.countryCode} />}
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{value.label}</Typography>
            </Box>
            <Field label="Description?"><YesNo value={value.hasDescription} /></Field>
            <Field label="Rich content?"><YesNo value={value.richContent} /></Field>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                <IconSeeker
                    icon={value.inStock ? 'circle-check' : 'circle-xmark'}
                    sx={{ fontSize: 13, color: value.inStock ? '#15803d' : '#b45309' }}
                />
                <Typography variant="caption" sx={{ color: value.inStock ? '#15803d' : '#b45309' }}>
                    {value.inStock ? 'In Stock' : 'Out of stock'}
                </Typography>
            </Box>
            <SellersChip
                info={value.offers}
                sellerName={value.sellerName}
                sellerCode={value.sellerCode}
                onOpen={() => onShowOffers(value.row)}
            />
        </Box>
    )
}


function ProductRawCell({ value, onShowDetails }) {
    if (!value) return <Missing />
    const stale = daysSince(value.date) > STALE_AFTER_DAYS

    return (
        <Box sx={{ display: 'flex', gap: 1.5, minWidth: 340 }}>
            {value.thumbnail
                ? <ImageSeeker
                    src={value.thumbnail}
                    title="Click to enlarge"
                    onClick={() => openImage(value.thumbnail, value.productTitle || value.productID)}
                    sx={{
                        width: 64, height: 64, objectFit: 'contain', flexShrink: 0,
                        cursor: 'zoom-in',
                    }}
                />
                : <Box sx={{
                    width: 64, height: 64, flexShrink: 0, borderRadius: 1,
                    border: '1px dashed #e5e7eb', display: 'flex',
                    alignItems: 'center', justifyContent: 'center'
                }}>
                    <IconSeeker icon="image-slash" sx={{ fontSize: 14, color: '#d1d5db' }} />
                </Box>}

            <Box sx={{ minWidth: 0 }}>
                {/* The title is the link to the retailer's page: that is where
                    anyone checking a scraping defect goes next, and it saves
                    turning on the Product URL column to get there. */}
                {value.productUrl
                    ? <Link
                        href={value.productUrl}
                        target="_blank"
                        rel="noreferrer"
                        underline="hover"
                        title={value.productTitle || value.productUrl}
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            fontWeight: 600, fontSize: 14, maxWidth: 320,
                        }}
                    >
                        <Box component="span" sx={{
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                            {value.productTitle || value.productID}
                        </Box>
                        <IconSeeker icon="arrow-up-right-from-square" sx={{ fontSize: 11, flexShrink: 0 }} />
                    </Link>
                    : <Typography
                        variant="body2"
                        title={value.productTitle || ''}
                        sx={{
                            fontWeight: 600, maxWidth: 320,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                        }}
                    >
                        {value.productTitle || <Missing />}
                    </Typography>}

                <Field label="Product ID:">
                    <Typography variant="caption">{value.productID}</Typography>
                </Field>

                <Field label="Brand raw:">
                    {/* '' is a real scraped value and the thing this screen is
                        for. Flag it, never tidy it into a normalised brand. */}
                    {value.brandRaw
                        ? <Typography variant="caption">{value.brandRaw}</Typography>
                        : <Chip
                            size="small"
                            label="empty"
                            sx={{ height: 16, fontSize: 10, color: '#b91c1c', backgroundColor: '#fef2f2' }}
                        />}
                </Field>

                <Field label="Last capture:">
                    <Typography
                        variant="caption"
                        sx={{ color: stale ? '#b45309' : 'inherit', fontWeight: stale ? 700 : 400 }}
                    >
                        {value.date || '—'}
                    </Typography>
                </Field>

                <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => onShowDetails(value.row)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, fontSize: 12 }}
                >
                    <IconSeeker icon="circle-info" sx={{ fontSize: 12 }} />
                    Show details
                </Link>
            </Box>
        </Box>
    )
}


/**
 * Every column, with the coverage percentages folded into the labels.
 *
 * Only six are on by default. The rest exist in the column chooser with their
 * percentage next to them, which is where the "what is this scraper missing?"
 * question is actually answered — a field sitting at 0.00% in that list IS the
 * finding, and it does not need a card of its own on screen to say so.
 */
//: The columns the backend will sort by — its SORTABLE whitelist. A composite
//: cell sorts by the field it leads with: Retailer info by site, Product Raw by
//: raw brand, which is the table's own default order.
const SORTABLE = new Set([
    'retailerInfo', 'productRaw', 'price', 'listPrice', 'shippingPrice',
    'numImages', 'numVideos', 'numRichContentImages', 'numRichContentVideos',
    'rating', 'numOpinions', 'lastReviewRating', 'lastReviewDate',
    'ean', 'reference', 'variantRaw', 'supertitle', 'subtitle', 'sellerName',
    'externalSeller', 'freeShipping', 'clickAndCollect', 'legacy', 'date',
    // Sort by what is behind the tick or the chip, not by the rendering: the
    // "has" columns by their text, the JSON ones by their length.
    'hasLastReviewTitle', 'hasRichContentText', 'gifts', 'eyecatchs', 'numGifts',
])


export function buildColumns(coverage, onShowDetails, display = {}, onShowOffers = () => {}) {
    // Whatever the user last chose wins over the column's own default. The
    // definitions are rebuilt on every response because the labels carry the
    // coverage percentages, and without this that rebuild would undo the
    // choice on each search.
    const shown = (name, fallback) => (display[name] === undefined ? fallback : display[name])

    const optional = (name, label, key, options = {}) => ({
        name,
        label: withCoverage(label, coverage, key ?? name),
        options: {
            filter: false,
            sort: SORTABLE.has(name),
            display: shown(name, false),
            customBodyRender: textCell,
            customHeadLabelRender: headerWithCoverage(label, coverage, key ?? name),
            setCellProps: centeredCell,
            setCellHeaderProps: centeredHead,
            ...options,
        },
    })

    return [
        {
            name: 'search_text',
            label: 'Search',
            options: { filter: false, sort: false, display: 'excluded', searchable: true },
        },
        {
            name: 'retailerInfo',
            label: 'Retailer info',
            options: {
                filter: false,
                sort: true,
                display: shown('retailerInfo', true),
                setCellProps: cellProps,
                setCellHeaderProps: headProps,
                customBodyRender: (value) => (
                    <RetailerInfoCell value={value} onShowOffers={onShowOffers} />
                ),
            },
        },
        {
            name: 'productRaw',
            label: 'Product Raw',
            options: {
                filter: false,
                // Sorts by the raw brand, which is what the cell leads with and
                // what the table's default order already uses.
                sort: true,
                searchable: true,
                display: shown('productRaw', true),
                setCellProps: cellProps,
                setCellHeaderProps: headProps,
                customBodyRender: (value) => (
                    <ProductRawCell value={value} onShowDetails={onShowDetails} />
                ),
            },
        },
        {
            name: 'price',
            label: withCoverage('Actual Price', coverage, 'actualPrice'),
            options: {
                filter: false,
                sort: true,
                display: shown('price', true),
                setCellProps: centeredCell,
                setCellHeaderProps: centeredHead,
                customHeadLabelRender: headerWithCoverage('Actual Price', coverage, 'actualPrice'),
                // Just the actual price. The list price used to hang under it
                // struck through, which duplicated a column that already exists
                // and made the cell two lines tall for no reason.
                customBodyRender: priceCell,
            },
        },
        {
            name: 'numImages',
            label: withCoverage('Images', coverage, 'numImages'),
            options: {
                filter: false,
                sort: true,
                display: shown('numImages', true),
                setCellProps: centeredCell,
                setCellHeaderProps: centeredHead,
                customHeadLabelRender: headerWithCoverage('Images', coverage, 'numImages'),
                customBodyRender: countCell,
            },
        },
        {
            name: 'rating',
            label: withCoverage('Rating', coverage, 'rating'),
            options: {
                filter: false,
                sort: true,
                display: shown('rating', true),
                setCellProps: centeredCell,
                setCellHeaderProps: centeredHead,
                customHeadLabelRender: headerWithCoverage('Rating', coverage, 'rating'),
                customBodyRender: (value) => value
                    // justifyContent, not the cell's text-align: a flex row
                    // ignores it and the stars sat on the left of the column.
                    ? <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <Rating value={Number(value)} precision={0.1} size="small" readOnly />
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            ({Number(value).toFixed(1)})
                        </Typography>
                    </Box>
                    : <Missing />,
            },
        },

        // --- everything below is off by default; the chooser carries the % ---
        optional('ean', 'EAN'),
        optional('reference', 'Reference'),
        optional('variantRaw', 'Variant raw'),
        optional('supertitle', 'Supertitle'),
        optional('subtitle', 'Subtitle'),
        optional('listPrice', 'List Price', 'listPrice', { customBodyRender: priceCell }),
        optional('shippingPrice', 'Shipping Price', 'shippingPrice', { customBodyRender: priceCell }),
        optional('sellerName', 'Seller Name'),
        optional('externalSeller', 'External Seller', 'externalSeller', { customBodyRender: flagCell }),
        optional('freeShipping', 'Free Shipping', 'freeShipping', { customBodyRender: flagCell }),
        optional('clickAndCollect', 'Click & Collect', 'clickAndCollect', { customBodyRender: flagCell }),
        optional('numVideos', 'Videos', 'numVideos', { customBodyRender: countCell }),
        optional('numRichContentImages', 'RC Images', 'numRichContentImages', { customBodyRender: countCell }),
        optional('numRichContentVideos', 'RC Videos', 'numRichContentVideos', { customBodyRender: countCell }),
        optional('hasRichContentText', 'Rich Content Text', 'richContentText', { customBodyRender: flagCell }),
        optional('numOpinions', 'Opinions', 'numOpinions', { customBodyRender: countCell }),
        optional('lastReviewRating', 'Last Review Rating', 'lastReviewRating'),
        optional('hasLastReviewTitle', 'Last Review Title', 'lastReviewTitle', { customBodyRender: flagCell }),
        optional('lastReviewDate', 'Last Review Date', 'lastReviewDate'),
        optional('eyecatchs', 'Eyecatchs', 'eyecatchs', {
            // The labels, not how many. "2" says a promo exists without saying
            // which, and which is the only reason to look at this column.
            customBodyRender: (value) => (
                value?.length
                    ? <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                        {value.map((label, i) => (
                            <Chip
                                key={`${label}-${i}`}
                                size="small"
                                label={label}
                                title={label}
                                sx={{ height: 18, fontSize: 10, maxWidth: 180 }}
                            />
                        ))}
                    </Box>
                    : <Missing />
            ),
        }),
        optional('gifts', 'Gifts', 'gifts', { customBodyRender: flagCell }),
        optional('numGifts', 'Gift Images', 'giftImages', { customBodyRender: countCell }),

        // Deliberately NOT columns, though the API returns them all:
        //   Item ID / Search ID / Task ID / Legacy — plumbing, and already in
        //     the drawer's Internal Info tab, where someone chasing one capture
        //     will actually look for them.
        //   Product URL — the product title IS the link now.
        //   Thumbnail URL — the thumbnail is rendered; the URL is not the data.
        //   Currency — it is printed next to the price it belongs to.
        // All six were 100 % coverage columns that said nothing about the
        // scraper, which is the only question this screen answers.
    ]
}
